import { useEffect, useMemo, useRef, useState } from "react";

import {
  CheckRoundIcon,
  Icon,
  PencilIcon,
  TickIcon,
} from "@vericus/cadmus-icons";
import { Button, colors, Switch, Text } from "@vericus/cadmus-ui";

import { useJitsu } from "@jitsu/react";
import { useParams } from "react-router-dom";

import assessmentImg from "@/assets/illustrations/edit-release.png";
import checkboxImg from "@/assets/illustrations/student-with-checkbox-list.png";
import { ApiError } from "@/components/errors";
import { LoadingPage } from "@/components/loading-page";
import { JitsuEvent } from "@/data/events/JitsuEvent";
import { getEntityId } from "@/features/peer-eval/utils/utils";
import {
  EntityType,
  PeerOverviewDocument,
  PeerOverviewQuery,
  ThreadFragment,
  useCompletePeerOutcomesMutation,
  useEntityThreadsQuery,
  usePeerOverviewQuery,
} from "@/generated/graphql";
import { getActiveMemberUsers } from "@/graphql/group/group-selectors";
import { useNavigationSource } from "@/lib/navigation-source";
import { useRootNavigate } from "@/routes";

import {
  CommentThreadsState,
  PeerOverviewTable,
} from "../components/peer-overview-table";
import {
  getPendingPeerEvaluations,
  isPeerEvalPastDue,
  isStudentPeerEvalComplete,
  PeerOutcomeTable,
  transformPeerOutcomesTable,
} from "../utils/utils";
import * as styles from "./home-page.css";

/**
 * Attributes each row's thread by the "given" convention (current viewer as
 * source, peer as target) — matches the mark form's own
 * `getEntityId(assessmentId, selfId, member.id)` call in `comment-table.tsx`.
 * Exported so the argument order is pinned by a unit test rather than only
 * exercised implicitly through the full page.
 */
export function buildCommentThreadsByUserId(
  threads: ThreadFragment[],
  peerOutcomeTable: PeerOutcomeTable,
  assessmentId: string,
  currentMarkerId: string
): Map<string, ThreadFragment> {
  const map = new Map<string, ThreadFragment>();
  peerOutcomeTable.forEach((row) => {
    const entityId = getEntityId(assessmentId, currentMarkerId, row.userId);
    const thread = threads.find((t) => t.entityId === entityId);
    if (thread) {
      map.set(row.userId, thread);
    }
  });
  return map;
}

export function PeerOverviewPageWithData() {
  const { assessmentId = "" } = useParams();
  const { track } = useJitsu();
  const { source, sourceType } = useNavigationSource();
  const { data, error, loading } = usePeerOverviewQuery({
    variables: {
      assessmentId,
    },
  });

  const hasTrackedOpen = useRef(false);
  useEffect(() => {
    if (!hasTrackedOpen.current && data) {
      hasTrackedOpen.current = true;
      track(JitsuEvent.PEER_EVALUATION_OPENED, {
        group_id: data.studentWorkGroup?.group?.id,
        source,
        source_type: sourceType,
      });
    }
  }, [data, track, source, sourceType]);

  const [completePeerOutcomes, { loading: loadingComplete }] =
    useCompletePeerOutcomesMutation({
      variables: {
        assessmentId,
      },
      refetchQueries: [
        { query: PeerOverviewDocument, variables: { assessmentId } },
      ],
      awaitRefetchQueries: true,
    });

  if (loading || loadingComplete) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingPage />
      </div>
    );
  }
  if (error || !data) return <ApiError />;

  return (
    <PeerOverviewPage
      data={data}
      assessmentId={assessmentId}
      onComplete={completePeerOutcomes}
    />
  );
}

interface Props {
  data: PeerOverviewQuery;
  assessmentId: string;
  onComplete: () => void;
}

/**
 * Page in Aphrodite HomePage with a overview table of the PeerOutcomes filled
 * by the current student, including self-evaluation.
 *
 * After every PeerOutcome is filled, the student can complete their Criteria
 * selections (aka CriterionOutcomes).
 */
function PeerOverviewPage(props: Props) {
  const { data, assessmentId, onComplete } = props;

  const groupPeerOutcomes = data.groupPeerOutcomes;
  const currentMarkerId = data.me.id;
  const peerRubric = data.peerRubric;
  const group = data.studentWorkGroup?.group;
  const selfEvaluationEnabled =
    data.assessment.settings?.selfEvaluationEnabled ?? false;

  const rootNavigate = useRootNavigate();

  // Toggle state for filtering pending evaluations
  const [filterPending, setFilterPending] = useState(false);

  const isPeerCompleted = useMemo(
    () => isStudentPeerEvalComplete(data.studentPeerEvalProgress),
    [data.studentPeerEvalProgress]
  );

  const { peerOutcomeTable, criteriaMap } = useMemo(() => {
    if (peerRubric === null || !group || group.activeGroupMembers.length === 0) {
      return { peerOutcomeTable: [], criteriaMap: [] };
    }
    return transformPeerOutcomesTable(
      groupPeerOutcomes,
      peerRubric.criteria,
      getActiveMemberUsers(group.activeGroupMembers),
      currentMarkerId,
      selfEvaluationEnabled
    );
  }, [
    groupPeerOutcomes,
    peerRubric,
    group,
    currentMarkerId,
    selfEvaluationEnabled,
  ]);

  // Batch-fetch the comment thread the current student gave each peer (and
  // themselves, for self-evaluation), so the overview table can show the
  // written feedback inline without opening the full mark form.
  const entityIds = useMemo(
    () =>
      peerOutcomeTable.map((row) =>
        getEntityId(assessmentId, currentMarkerId, row.userId)
      ),
    [peerOutcomeTable, assessmentId, currentMarkerId]
  );

  const {
    data: threadsData,
    loading: threadsLoading,
    error: threadsError,
  } = useEntityThreadsQuery({
    variables: {
      entityType: EntityType.PeerOutcome,
      entityIds,
    },
    skip: entityIds.length === 0,
  });

  const commentThreadsState: CommentThreadsState = useMemo(() => {
    if (threadsLoading) return { status: "loading" };
    if (threadsError) return { status: "error" };
    return {
      status: "ready",
      threadsByUserId: buildCommentThreadsByUserId(
        threadsData?.entityThreads ?? [],
        peerOutcomeTable,
        assessmentId,
        currentMarkerId
      ),
    };
  }, [
    threadsLoading,
    threadsError,
    threadsData,
    peerOutcomeTable,
    assessmentId,
    currentMarkerId,
  ]);

  const handleDownload = () => {
    const csvContent = convertToCSV(peerOutcomeTable);
    const blob = new Blob([csvContent]);

    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `peer_evaluation_group_${group?.id}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const { pendingMarkIds, totalPendingEvaluations, actualTotalEvaluations } =
    useMemo(
      () =>
        getPendingPeerEvaluations(
          data,
          !!data.assessment.settings?.selfEvaluationEnabled
        ),
      [data]
    );

  const arePeerEvaluationsPending = pendingMarkIds.length > 0;

  // Computed on every render (cheap) rather than memoized on the due date, so
  // the lock reliably flips to `true` once the page crosses the due instant.
  const isPastDue = isPeerEvalPastDue({
    peerEvaluationDueDate: data.assessment.settings?.peerEvaluationDueDate,
    assessmentDueDate: data.assessment.settings?.dueDate,
    workExtensionDueDate: data.studentWorkGroup?.work?.settings?.dueDate,
  });

  // Go to the main PeerEvaluation page, focused on an optional student.
  // Land on the Instructions tab unless the student has already started
  // evaluating (given at least one outcome), in which case go straight to
  // the Evaluation tab.
  const handleMarkStudent = (studentId?: string) => {
    const studentIdSearch = studentId ? `&student=${studentId}` : "";
    const tab = actualTotalEvaluations > 0 ? "evaluation" : "instructions";
    rootNavigate(`/peer-evaluation?tab=${tab}${studentIdSearch}`);
  };

  // If the Group is not formed yet, we do not show the PeerOverviewPage
  const isGroupWorkDefined = !!data?.studentWorkGroup?.id;
  if (!isGroupWorkDefined) {
    return <HiddenAssessment />;
  }

  // Charter blocks peer evaluation once usable (enabled AND a published form,
  // folded server-side into `groupCharterAvailable`) until this student has
  // accepted it.
  if (
    data.assessment.settings?.groupCharterAvailable &&
    !data.studentWorkGroup?.charter?.latestAgreement
  ) {
    return <CharterNotAccepted />;
  }

  if (!data?.peerRubric?.id || actualTotalEvaluations < 1) {
    return (
      <>
        <div className={styles.peerContent}>
          <img src={checkboxImg} height={320} width={320} alt="" />
          <div className={styles.peerTextWrapper}>
            <Text kind="headingFour">Get Started with Peer Evaluation</Text>
            <Text kind="bodyLine">
              Your peer evaluation is available. Start now to give and receive
              valuable feedback on your group’s collaboration and your own
              contributions.
            </Text>
            <div>
              <Button
                style={{ marginTop: 12 }}
                kind="secondary"
                disabled={!isGroupWorkDefined}
                onClick={() => handleMarkStudent()}
              >
                Start peer evaluation
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ width: "100%" }}>
        {!isPeerCompleted ? (
          <div className={styles.peerHeader}>
            <Text kind="headingFour">
              {totalPendingEvaluations === 0
                ? "All evaluations completed"
                : `${totalPendingEvaluations} Evaluation${totalPendingEvaluations !== 1 ? "s" : ""} pending`}
            </Text>
            <div className={styles.filtersContainer}>
              {arePeerEvaluationsPending && (
                <Switch
                  label="Pending only"
                  labelPosition="right"
                  checked={filterPending}
                  onCheckedChange={(checked) => setFilterPending(checked)}
                />
              )}
              {!isPastDue && (
                <>
                  <Button
                    icon={<PencilIcon label="" size={24} />}
                    onClick={() => handleMarkStudent()}
                  >
                    Edit ratings and feedback
                  </Button>
                  <Button
                    icon={<TickIcon label="" size={24} />}
                    disabled={arePeerEvaluationsPending}
                    onClick={onComplete}
                  >
                    Finalise and submit
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div className={styles.completedContainer}>
              <CheckRoundIcon label="" color={colors.success} />
              <Text kind="headingFive">
                Your peer evaluations are complete.
              </Text>
            </div>
            <Button
              onClick={handleDownload}
              icon={<Icon iconName="Download" />}
            >
              Download
            </Button>
          </div>
        )}
      </div>
      <div className={styles.peerTableContainer}>
        <PeerOverviewTable
          me={data.me}
          filterByPending={filterPending}
          headers={criteriaMap}
          pendingMarkIds={pendingMarkIds}
          rows={peerOutcomeTable}
          disableEdit={isPastDue}
          onMarkStudent={handleMarkStudent}
          onEditStudent={handleMarkStudent}
          commentThreads={commentThreadsState}
        />
      </div>
    </>
  );
}

function HiddenAssessment() {
  return (
    <div className={styles.root} style={{ alignItems: "center" }}>
      <div className={styles.emptyContent}>
        <img src={assessmentImg} height={280} width={280} alt="" />
        <Text kind="headingThree" textAlign="center">
          Peer Evaluation will be visible once group formation is complete
        </Text>
        <Text kind="bodyLine" textAlign="center">
          Provide feedback on your team members&apos; contributions and receive
          input to improve collaboration and project results.
        </Text>
      </div>
    </div>
  );
}

function CharterNotAccepted() {
  return (
    <div className={styles.root} style={{ alignItems: "center" }}>
      <div className={styles.emptyContent}>
        <img src={assessmentImg} height={280} width={280} alt="" />
        <Text kind="headingThree" textAlign="center">
          Peer Evaluation will be visible once you accept the group charter
        </Text>
        <Text kind="bodyLine" textAlign="center">
          Provide feedback on your team members&apos; contributions and receive
          input to improve collaboration and project results.
        </Text>
      </div>
    </div>
  );
}

function convertToCSV(data: PeerOutcomeTable) {
  const criteriaOrder: { id: string; label: string }[] = [];
  data.forEach((user) => {
    user.outcomes.forEach((o) => {
      if (!criteriaOrder.some((c) => c.id === o.criteriaId)) {
        criteriaOrder.push({ id: o.criteriaId, label: o.criteriaValue });
      }
    });
  });

  // Build header rows
  const header1 = [
    "Group members",
    ...criteriaOrder.map((_, i) => `Criteria ${i + 1}`),
    "Status",
  ];
  const header2 = ["", ...criteriaOrder.map((c) => c.label), ""];

  const rows: string[][] = [];
  rows.push(header1);
  rows.push(header2);

  data.forEach((user) => {
    const row: string[] = [user.userName];
    criteriaOrder.forEach((criteria) => {
      const outcome = user.outcomes.find((o) => o.criteriaId === criteria.id);
      row.push(outcome ? outcome.scaleValue : "");
    });
    row.push("Done");
    rows.push(row);
  });

  // Convert to CSV
  return rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
}
